import { configureStore } from '@reduxjs/toolkit';
import { Map } from 'immutable';
import createSagaMiddleware from 'redux-saga';

import rootSaga from './rootSaga';
import rootReducer from './rootReducer';

const sagaMiddleware = createSagaMiddleware();

/**
 * Security Note: visitorApp Standalone Redux Store
 *
 * This store lacks the security sanitizers present in the main app's shared store.
 * DevTools sanitizers mask sensitive data (tokens, passwords, PII) during development.
 *
 * FUTURE: Migrate to React Query to eliminate Redux entirely, per AGENTS.md guidelines.
 */
const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).concat(sagaMiddleware),
  devTools: process.env.NODE_ENV !== 'production' && {
    stateSanitizer: (state: any) => {
      // Convert Immutable state to plain JS for sanitization
      const stateJS = state.toJS ? state.toJS() : state;

      return {
        ...stateJS,
        // Mask authentication tokens
        accessToken: stateJS.accessToken ? '<<REDACTED>>' : stateJS.accessToken,
        // Mask Alta Video credentials
        altaVideo: stateJS.altaVideo
          ? {
              ...stateJS.altaVideo,
              altaVideoIntegrationData:
                stateJS.altaVideo.altaVideoIntegrationData?.map(
                  (item: any) => ({
                    ...item,
                    msiAltaVideoUsername: '<<REDACTED>>',
                    msiAltaVideoPassword: '<<REDACTED>>',
                  }),
                ),
            }
          : stateJS.altaVideo,
        // Mask visitor PII
        visitors: stateJS.visitors
          ? { ...stateJS.visitors, data: '<<REDACTED - Contains PII>>' }
          : stateJS.visitors,
        // Mask user information
        users: stateJS.users
          ? { ...stateJS.users, data: '<<REDACTED - Contains PII>>' }
          : stateJS.users,
        // Mask invitation emails
        visitorInvitation: stateJS.visitorInvitation
          ? {
              ...stateJS.visitorInvitation,
              data: '<<REDACTED - Contains PII>>',
            }
          : stateJS.visitorInvitation,
        // Mask agreement data
        agreements: stateJS.agreements
          ? { ...stateJS.agreements, data: '<<REDACTED - Contains PII>>' }
          : stateJS.agreements,
      };
    },
    actionSanitizer: (action: any) => {
      // Mask actions containing sensitive payloads
      if (
        action.type?.toLowerCase().includes('login') ||
        action.type?.toLowerCase().includes('password') ||
        action.type?.toLowerCase().includes('token') ||
        action.type?.toLowerCase().includes('credential')
      ) {
        return {
          ...action,
          payload: '<<REDACTED>>',
        };
      }
      return action;
    },
  },
});

sagaMiddleware.run(rootSaga);

export type RootState = ReturnType<typeof rootReducer>;
export type ImmutableRootState = Map<keyof RootState, unknown>;
export type AppDispatch = typeof store.dispatch;

export default store;
